import React, { useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { Colors } from 'libs/end-to-end/src/types/branding/color';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { CHART_COLORS } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/ChartColors';
import { SingleMetricTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GenericTableViewTooltip';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { PieChartPlaceholder } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/PieChartPlaceholder';

/**
 * Data structure for individual pie chart segments
 */
interface PieChartDataItem {
  /** Unique identifier for the pie segment (e.g., "Bachelor's degree") */
  id: string;
  /** Display label for the pie segment (same as id) */
  label: string;
  /** Numeric value for the pie segment */
  value: number;
  /** Average-based value used for tooltip display */
  averageValue: number;
  /** Original raw value from table data (string format) */
  rawValue: string;
  /** Sample size for the pie segment */
  sampleSize: number;
  /** Numerator size for the pie segment */
  numeratorSize: number;
}

/**
 * Pie chart grid component for visualizing TableView data using Nivo.
 *
 * Features:
 * - Creates a grid of pie charts, one for each metric
 * - Each pie shows percentage breakdown by groupBy categories (e.g., education levels)
 * - Responsive grid layout that adapts to number of metrics
 * - Custom tooltips with formatted values
 * - Consistent theming with app design system
 *
 * Data transformation:
 * - Each metric becomes one pie chart
 * - Pie slices represent groupBy categories (education levels)
 * - Values show distribution of that metric across categories
 *
 * @example
 * ```tsx
 * <PieChartGridMode
 *   data={tableData}
 *   visibleRows={visibleRows}
 *   viewConfiguration={{
 *     colorScheme: 'custom',
 *   }}
 * />
 * ```
 */
/**
 * Color schemes for pie chart segments
 */
const COLOR_SCHEMES = {
  lightGray: '#E7E8E8',
  default: Colors, // Use existing centralized colors
  custom: CHART_COLORS,
};

export const PieChartGridMode: React.FC<TableViewModeArguments> = ({
  data,
  visibleRows,
  viewConfiguration,
}) => {
  const { colorScheme = 'default' } = viewConfiguration;
  const displayOverallCount = viewConfiguration.overallDisplayMode === 'count';
  const { getColumnDisplayName } = useGetColumnDisplayName();

  const language = useLanguage();

  // Get groupBy categories (education levels) from first column values
  const groupByCategories = useMemo(() => {
    const rows = data.rows;
    return rows.map((row) => row[0]);
  }, [data]);

  // Get metric columns (columnHeaders contains only metrics, groupBy dimension not included)
  const metricColumns = useMemo(() => {
    return data.columnHeaders;
  }, [data]);

  // Transform data into pie chart format for each metric
  const pieChartDataByMetric = useMemo(() => {
    const result: Record<string, PieChartDataItem[]> = {};

    const rows = data.rows;
    const columnHeaders = data.columnHeaders;

    metricColumns.forEach((metricColumn) => {
      const metricColumnIndex = columnHeaders.indexOf(metricColumn);

      const pieData = groupByCategories.map((categoryValue, rowIndex) => {
        const rawValue = rows[rowIndex][metricColumnIndex + 1]; // +1 because row[0] is groupBy dimension
        const averageValue = safeParseFloat(rawValue) || 0;
        const numeratorSize =
          data.numeratorSizes[rowIndex]?.[metricColumnIndex + 1] ?? 0;

        return {
          id: categoryValue,
          label: categoryValue,
          value: displayOverallCount ? numeratorSize : averageValue,
          averageValue,
          rawValue: rawValue,
          sampleSize: data.sampleSizes[rowIndex][metricColumnIndex + 1],
          numeratorSize,
        };
      });

      const unselectedRowsItem = pieData.reduce(
        (acc, row, rowIndex) => {
          if (!visibleRows.includes(rowIndex) && visibleRows.length > 0) {
            acc.value += displayOverallCount
              ? row.numeratorSize
              : row.averageValue;
            acc.averageValue += row.averageValue;
            acc.rawValue = `${acc.value}`;
            acc.sampleSize += row.sampleSize;
            acc.numeratorSize += row.numeratorSize;
          }
          return acc;
        },
        {
          id: 'unselected',
          label: language.unselected(),
          value: 0,
          averageValue: 0,
          rawValue: '0',
          sampleSize: 0,
          numeratorSize: 0,
        },
      );

      result[metricColumn] = [
        ...pieData.filter(
          (_, index) => visibleRows.includes(index) || visibleRows.length === 0,
        ),
        unselectedRowsItem,
      ];
    });

    return result;
  }, [
    metricColumns,
    data,
    groupByCategories,
    language,
    visibleRows,
    displayOverallCount,
  ]);

  // Color scheme for pie slices - supports multiple color schemes
  const colors = useMemo(() => {
    if (colorScheme === 'custom') {
      // Use custom hex colors directly
      return groupByCategories.map((_, index) => {
        if (!visibleRows.includes(index) && visibleRows.length > 0) {
          return COLOR_SCHEMES.lightGray;
        }
        return COLOR_SCHEMES.custom[index % COLOR_SCHEMES.custom.length];
      });
    } else {
      // Use centralized color system (default)
      return groupByCategories.map((_, index) => {
        if (!visibleRows.includes(index) && visibleRows.length > 0) {
          return COLOR_SCHEMES.lightGray;
        }

        const colorName = Colors[index % Colors.length];
        return ConfigColors[colorName].Primary.raw;
      });
    }
  }, [groupByCategories, colorScheme, visibleRows]);

  const filteredColors = useMemo(() => {
    return [
      ...colors.filter(
        (_, index) => visibleRows.includes(index) || visibleRows.length === 0,
      ),
      COLOR_SCHEMES.lightGray,
    ];
  }, [colors, visibleRows]);

  // Smart grid configuration based on number of metrics
  const gridConfig = useMemo(() => {
    const numMetrics = metricColumns.length;

    if (numMetrics === 1) {
      return {
        cols: 'grid-cols-1',
        height: 'h-[300px]',
        gridCols: 1,
      };
    }
    if (numMetrics === 2) {
      return {
        cols: 'grid-cols-1 md:grid-cols-2',
        height: 'h-[240px]',
        gridCols: 2,
      };
    }
    if (numMetrics === 3) {
      return {
        cols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
        height: 'h-[240px]',
        gridCols: 3,
      };
    }
    if (numMetrics <= 4) {
      return {
        cols: 'grid-cols-1 md:grid-cols-2',
        height: 'h-[180px]',
        gridCols: 2,
      };
    }
    if (numMetrics <= 6) {
      return {
        cols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
        height: 'h-[180px]',
        gridCols: 3,
      };
    }
    // 7+ metrics
    return {
      cols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
      height: 'h-[180px]',
      gridCols: 4,
    };
  }, [metricColumns.length]);

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  if (metricColumns.length === 0) {
    return (
      <div className="w-full h-[400px] bg-white flex items-center justify-center text-gray-500">
        No metrics available for pie chart grid
      </div>
    );
  }

  return (
    <div className="w-full bg-white p-4">
      {/* Single horizontal legend at top */}
      <div className="border-b pb-4 mb-6">
        <div className="flex justify-center">
          <div className="flex flex-wrap gap-4 justify-center">
            {groupByCategories.map(
              (category, index) =>
                (visibleRows.includes(index) || visibleRows.length === 0) && (
                  <div key={category} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: colors[index] }}
                    />
                    <span className="text-sm text-gray-700">{category}</span>
                  </div>
                ),
            )}
            {visibleRows.length < data.rows.length &&
              visibleRows.length > 0 && (
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: COLOR_SCHEMES.lightGray }}
                  />
                  <span className="text-sm text-gray-700">
                    {language.unselected()}
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Pie charts grid */}
      <div className={`grid gap-6 ${gridConfig.cols}`}>
        {metricColumns.map((metricColumn, index) => {
          const pieData = pieChartDataByMetric[metricColumn];

          // Calculate if this item is last in its row for border logic
          const effectiveCols = Math.min(
            gridConfig.gridCols,
            metricColumns.length,
          );
          const isLastInRow = (index + 1) % effectiveCols === 0;

          const metricDefinition = getMetricAndFocusAreaFromColumnName({
            columnName: metricColumn,
          })?.metric;

          const noGroupNoBooleanMetric =
            !metricDefinition?.isMetricBoolean && !data.selectedGroupBy;

          if (noGroupNoBooleanMetric) {
            return (
              <PieChartPlaceholder
                key={metricColumn}
                message={language.metricRequiresBreakdown()}
                metricColumn={metricColumn}
                isLastInRow={isLastInRow}
              />
            );
          }

          // if metric is not boolean and overall display mode is count, show placeholder instead
          if (
            viewConfiguration.overallDisplayMode === 'count' &&
            !metricDefinition?.isMetricBoolean
          ) {
            return (
              <PieChartPlaceholder
                key={metricColumn}
                metricColumn={metricColumn}
                isLastInRow={isLastInRow}
                message={language.cannotDisplayCountsForMetric()}
              />
            );
          }

          return (
            <div
              key={metricColumn}
              className={`bg-white relative ${!isLastInRow ? 'border-r border-[#D9D9D9]' : ''}`}
            >
              {/* Metric title */}
              <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">
                {getColumnDisplayName(metricColumn)}
              </h3>

              {/* Pie chart - dynamically sized based on grid density */}
              <div className={`w-full ${gridConfig.height}`}>
                <ResponsivePie
                  data={pieData}
                  margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                  innerRadius={0.4}
                  padAngle={2}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  colors={filteredColors}
                  borderWidth={0}
                  // Remove all labels for cleaner look
                  enableArcLabels={false}
                  enableArcLinkLabels={false}
                  tooltip={(datum) => (
                    <SingleMetricTooltip
                      metricColumn={metricColumn}
                      color={datum.datum.color}
                      percentageValue={datum.datum.data.averageValue}
                      levelDisplayName={String(datum.datum.label)}
                      totalSubjects={datum.datum.data.sampleSize}
                      numeratorSize={datum.datum.data.numeratorSize}
                      units={viewConfiguration.units}
                      overallDisplayMode={viewConfiguration.overallDisplayMode}
                    />
                  )}
                  theme={{
                    tooltip: {
                      container: {
                        background: '#ffffff',
                        color: '#333333',
                        fontSize: 12,
                        fontFamily: KARLA_FONT_FAMILY,
                      },
                    },
                  }}
                  animate={true}
                  motionConfig="gentle"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
